import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["title", "Pack title"], ["subject", "Subject or occupation"], ["textbook_title", "Source textbook title"],
  ["edition", "Edition (e.g. 2nd edition (2e))"], ["edition_number", "Edition number", "number"], ["version", "Pack version"],
  ["authors", "Authors / editors"], ["publisher", "Publisher"], ["source_url", "Source glossary URL"], ["book_url", "Book URL (optional)"],
  ["license_name", "License name"], ["license_url", "License URL"],
];
const EMPTY = Object.fromEntries([...FIELDS.map(([k]) => [k, ""]), ["attribution_text", ""], ["description", ""]]);

export const PackFormDialog = ({ open, onOpenChange, pack, onSaved, prefill }) => {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (open) setForm(pack ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, pack[k] ?? ""])) : { ...EMPTY, edition_number: 1, version: "1.0.0", ...(prefill || {}) });
  }, [open, pack]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const body = { ...form, edition_number: Number(form.edition_number) || 1 };
      if (pack) await api.put(`/admin/packs/${pack.id}`, body);
      else await api.post("/admin/packs", body);
      toast.success(pack ? "Pack updated" : "Draft pack created");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pack-form-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{pack ? "Edit pack details" : "New Gloss Pack"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label, type]) => (
            <div key={k} className="space-y-1.5">
              <Label>{label}</Label>
              <Input data-testid={`pack-form-${k.replace(/_/g, "-")}-input`} type={type || "text"} value={form[k]} onChange={set(k)} />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2"><Label>Required attribution text (verbatim)</Label><Textarea data-testid="pack-form-attribution-text-input" rows={3} value={form.attribution_text} onChange={set("attribution_text")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea data-testid="pack-form-description-input" rows={3} value={form.description} onChange={set("description")} /></div>
          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="pack-form-tooltip-preview">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">What students see when they hover a term</p>
            <div className="mt-2 inline-block max-w-md rounded-lg bg-[#1a2540] px-3 py-2 text-[13px] leading-snug text-white">
              <mark className="rounded-sm bg-yellow-200 px-1 font-semibold text-slate-900">{form.textbook_title?.trim() ? "A term" : "Term"}</mark>
              {" "}The textbook's exact definition goes here. — <span data-testid="pack-form-source-line">Source: {form.title.split(" — ")[0].trim() || "[pack title]"} · {form.license_name.split(" (")[0].trim() || "[license]"}</span>
            </div>
            <p className="mt-2 text-xs text-slate-500">Built from the pack title (the part before " — ") and the license name. In the GlossLink popup this pack is listed as <b data-testid="pack-form-glossary-name">{form.textbook_title.trim() || "[textbook title]"} {Number(form.edition_number) || 1}e</b>.</p>
          </div>
        </div>
        <DialogFooter><Button data-testid="pack-form-save-button" onClick={save} className="rounded-full px-6">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
